import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';
import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import { productRulesFilters } from './credit-product-rules.filters';

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela da regra para executar esta ação.`);
};

export const RULES_PATH = '/cadastro/configuracao-regras';
/** O legado cria com o id `nova` (CreditProductRulesFormContainer.tsx:43). */
export const NEW_RULE_ID = 'nova';
export const rulePath = (id: string) => `${RULES_PATH}/${encodeURIComponent(id)}`;
export const ruleProductsPath = (id: string) => `${rulePath(id)}/produtos`;

const idInput = z.object({ id: z.string().min(1) });

export const productRulesCreate = action({
  id: 'creditProductRules.create',
  kind: 'draft',
  label: msg`Nova regra`,
  description: msg`Abre o formulário em branco para criar um conjunto de regras de crédito ou de compliance.`,
  permission: can('CreditProductRules', 'Create'),
  run: ({ nav }) => nav.to(rulePath(NEW_RULE_ID)),
});

export const productRulesOpen = action({
  id: 'creditProductRules.open',
  kind: 'navigate',
  label: msg`Abrir`,
  description: msg`Abre um conjunto de regras pelo id, com a identificação, a descrição e os parâmetros.`,
  row: true,
  page: true,
  input: idInput,
  run: ({ nav, input }) => nav.to(rulePath(input.id)),
});

export const productRulesListRefresh = action({
  id: 'creditProductRules.list.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca a lista de regras de novo, sem mudar nada.`,
  run: mountedScreenRequired,
});

export const productRulesFilter = filterAction('creditProductRules.filter', productRulesFilters, {
  description: msg`Filtra a lista de regras: searchString (busca livre), name (nome) e rulesType (tipo de regra: 1 compliance, 2 crédito). Filtro omitido é limpo.`,
});

export const productRulesListPage = listPageAction('creditProductRules.list.page');

export const productRulesListScreen = defineScreen({
  id: 'creditProductRules.list',
  path: RULES_PATH,
  title: msg`Central de Regras`,
  description: msg`Central de Regras: lista dos conjuntos de regras de crédito e de compliance usados pelos produtos de crédito, com busca, filtro por tipo, paginação, abrir e criar.`,
  permission: can('CreditProductRules', 'Read'),
  place: { section: 'register', icon: 'badge-check' },
  actions: [
    productRulesCreate,
    productRulesListRefresh,
    productRulesFilter,
    productRulesListPage,
    productRulesOpen,
  ],
});

export const productRuleBack = action({
  id: 'creditProductRules.back',
  kind: 'navigate',
  label: msg`Central de Regras`,
  description: msg`Sai da regra e mostra a lista da Central de Regras de novo.`,
  run: ({ nav }) => nav.to(RULES_PATH),
});

export const productRuleRefresh = action({
  id: 'creditProductRules.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados da regra aberta, sem mudar nada.`,
  run: mountedScreenRequired,
});

export const productRuleHistory = action({
  id: 'creditProductRules.history',
  kind: 'navigate',
  label: msg`Histórico`,
  description: msg`Abre o painel com o histórico de alterações da regra aberta.`,
  run: mountedScreenRequired,
});

export const productRuleProducts = action({
  id: 'creditProductRules.products',
  kind: 'navigate',
  label: msg`Produtos Afetados`,
  description: msg`Abre a lista dos produtos de crédito que usam esta regra. Recebe o tipo da regra (ruleType: 1 compliance, 2 crédito).`,
  input: idInput.extend({ ruleType: z.number().int() }),
  // Número, não texto: o roteador serializa texto em JSON (`?ruleType=%221%22`) e o legado lê
  // `produtos?ruleType=1` (FormHeader.tsx:109; RulesProductListContainer.tsx:25-26). Revisão do !11106.
  run: ({ nav, input }) => nav.to(ruleProductsPath(input.id), { search: { ruleType: input.ruleType } }),
});

export const productRuleSave = action({
  id: 'creditProductRules.save',
  kind: 'write',
  label: msg`Salvar regra`,
  description: msg`Confere e salva a regra aberta, com o nome, o tipo, a descrição e os parâmetros que estão no formulário. Todos os produtos ligados à regra passam a seguir a versão salva. Pede a confirmação da pessoa.`,
  permission: can('CreditProductRules', 'Update'),
  preview: () => ({
    title: t`Salvar regra`,
    detail: t`Ao salvar, todos os produtos vinculados a esta regra são atualizados.`,
  }),
  run: mountedScreenRequired,
});

export const productRuleDetailScreen = defineScreen({
  id: 'creditProductRules.detail',
  path: `${RULES_PATH}/$ruleId`,
  title: msg`Regra`,
  description: msg`Cadastro de uma regra (nova ou edição): identificação, descrição e parâmetros de crédito ou de compliance; salvar, atualizar, histórico e produtos afetados.`,
  permission: can('CreditProductRules', 'Read'),
  place: { parent: 'creditProductRules.list', icon: 'file-text', record: msg`Regra` },
  actions: [productRuleBack, productRuleRefresh, productRuleHistory, productRuleProducts, productRuleSave],
});

export const ruleProductsRefresh = action({
  id: 'creditProductRules.products.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de produtos que usam a regra, sem mudar nada.`,
  run: mountedScreenRequired,
});

export const ruleProductsAdd = action({
  id: 'creditProductRules.products.add',
  kind: 'write',
  label: msg`Adicionar novo produto`,
  description: msg`Liga um produto de crédito a esta regra: o produto passa a seguir os parâmetros dela. Pede a confirmação da pessoa.`,
  input: z.object({ creditProductId: z.string().min(1), creditProductName: z.string().optional() }),
  preview: ({ creditProductName, creditProductId }) => ({
    title: t`Adicionar produto à regra`,
    detail: t`O produto ${creditProductName ?? creditProductId} passa a usar esta regra.`,
  }),
  run: mountedScreenRequired,
});

export const ruleProductsOpenProduct = action({
  id: 'creditProductRules.products.openProduct',
  kind: 'navigate',
  label: msg`Ver produto`,
  description: msg`Abre o cadastro de um produto de crédito que usa esta regra.`,
  row: true,
  page: true,
  input: idInput,
  run: ({ nav, input }) => nav.to(`/cadastro/produtos-credito/${encodeURIComponent(input.id)}`),
});

export const ruleProductsPage = listPageAction('creditProductRules.products.page');

export const ruleProductsScreen = defineScreen({
  id: 'creditProductRules.products',
  path: `${RULES_PATH}/$ruleId/produtos`,
  title: msg`Produtos da regra`,
  description: msg`Produtos de crédito que usam uma regra, com paginação, abrir o produto e adicionar outro produto à regra.`,
  permission: can('CreditProductRules', 'Read'),
  place: { parent: 'creditProductRules.detail', icon: 'folder-cog' },
  actions: [ruleProductsRefresh, ruleProductsAdd, ruleProductsOpenProduct, ruleProductsPage],
});
